const API_BASE = "/api";


export async function fetchPackages(query = '', signal) {
  const url = query
    ? `${API_BASE}/p-route/search?query=${encodeURIComponent(query)}`
    : `${API_BASE}/p-route/default`;
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  const data = await response.json();
  if (!Array.isArray(data) || !data.every(pkg => typeof pkg.package_name === 'string')) {
    throw new Error('Invalid packages data format');
  }
  return data;
}

export async function fetchSuggestions(query, signal) {
  const url = `${API_BASE}/p-route/suggestions?query=${encodeURIComponent(query)}`;
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  const data = await response.json();
  if (!Array.isArray(data) || !data.every(pkg => typeof pkg.package_name === 'string')){
    throw new Error('Invalid suggestions data format');
  }
  return data;
}

export async function fetchPackagesByTags(tags, signal) {
  if (!tags.length) return [];
  const url = `${API_BASE}/p-route/filter?tags=${encodeURIComponent(tags.join(','))}`;
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  const data = await response.json();
  if (!Array.isArray(data) || !data.every(pkg => typeof pkg.package_name === 'string')) {
    throw new Error('Invalid packages data format');
  }
  return data;
}

export async function fetchTags(signal) {
  const url = `${API_BASE}/p-route/get_tags`;
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
  const data = await response.json();
  if (!Array.isArray(data) || !data.every(tag => 'tag' in tag && 'category' in tag)) {
    console.warn('[fetchTags] Invalid response:', JSON.stringify(data, null, 2));
    throw new Error('Invalid tags data format');
  }
  return data;
}


// export async function sendEmailApi(formData, signal) {
//   if (!formData || !Object.keys(formData).length) {
//     return { success: false, error: 'Form data is empty' };
//   }

//   const url = `${API_BASE}/send-email`;
//   try {
//     const response = await fetch(url, {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/json',
//       },
//       body: JSON.stringify({
//         to: formData.email,
//         subject: `Travel Inquiry: ${formData.destination}`,
//         html: `
//           <p><strong>Travel Inquiry</strong></p>
//           <p><strong>Name:</strong> ${formData.name}</p>
//           <p><strong>Email:</strong> ${formData.email}</p>
//           <p><strong>Contact Number:</strong> ${formData.contactNumber}</p>
//           <p><strong>Destination:</strong> ${formData.destination}</p>
//           <p><strong>Travel Date:</strong> ${formData.travelDate?.format('YYYY-MM-DD') || 'Not specified'}</p>
//           <p><strong>Stay Days:</strong> ${formData.stayDays || 'Not specified'}</p>
//           <p><strong>Adults:</strong> ${formData.adults}</p>
//           <p><strong>Children:</strong> ${formData.children}</p>
//           <p><strong>Infants:</strong> ${formData.infants}</p>
//           <p><strong>Departure City:</strong> ${formData.departureCity || 'Not specified'}</p>
//           <p><strong>Budget:</strong> ${formData.budget ? `₹${formData.budget}` : 'Not specified'}</p>
//           <p><strong>Special Requests:</strong> ${formData.specialRequests || 'None'}</p>
//         `,
//       }),
//       signal,
//     });

//     if (!response.ok) {
//       const data = await response.json();
//       if (response.status === 429) {
//         throw new Error('Too many requests. Please try again in 20 minutes.');
//       }
//       throw new Error(data.error || `HTTP Error! Status: ${response.status}`);
//     }

//     const data = await response.json();
//     return data;
//   } catch (error) {
//     if (error.name !== 'AbortError') {
//       throw error;
//     }
//     return { success: false, error: 'Request aborted' };
//   }
// }

export async function fetchItinerary(query, signal) {

  const decodedQuery = decodeURIComponent(query);
  const url = `${API_BASE}/p-route/itinerary-packages?query=${encodeURIComponent(decodedQuery)}`;
  try {
    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();
    if (Array.isArray(data)) {
      if (!data.length) {
        throw new Error('Invalid suggestions data format: Each item must have a package_name');
      }
      return data[0];
    }

    throw new Error('Invalid data format: Expected an array or object with result array');
  } catch (error) {
    if (error.name === 'AbortError') {
      throw error;
    }
    console.error('Fetch itinerary error:', error.message);
    throw new Error(`Failed to fetch itinerary: ${error.message}`);
  }
}

export async function packageCheckUp(query, signal, excludeId) {
  if (!query) {
    throw new Error('Query cannot be empty');
  }

  const url = `${API_BASE}/admin/check-package-name?package_name=${encodeURIComponent(query)}&exclude_id=${excludeId || ''}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    if (typeof data !== 'object' || data === null || !('count' in data)) {
      throw new Error('Invalid response format');
    }
    return { count: parseInt(data.count, 10) || 0 };
  } catch (error) {
    if (error.name === 'AbortError') {
      throw error;
    }
    console.error('Package check error:', error.message);
    throw new Error(`Failed to check package name: ${error.message}`);
  }
}

export const uploadImages = async (files, packageId, signal) => {
  const formData = new FormData();
  files.forEach(({ file, position }, index) => {
    if (!file || !position) throw new Error(`Invalid file or position at index ${index}`);
    formData.append('images', file);
    formData.append('positions', position);
  });

  try {
    const response = await fetch(`${API_BASE}/admin/update-package-images/${packageId}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      signal,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to upload images');
    return { ...data, status: response.status };
  } catch (error) {
    throw new Error(`Upload failed: ${error.message}`);
  }
};

export const uploadImagesForAddPackage = async (files, packageData, signal) => {
  const formData = new FormData();
  files.forEach((item, index) => {
    if (item.file instanceof File) {
      formData.append('images', item.file);
      formData.append('positions[]', item.position.toString());
    }
  });
  formData.append('packageData', JSON.stringify(packageData));
  try {
    const response = await fetch(`${API_BASE}/admin/upload-image`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      signal,
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to upload images');
    return { ...data, status: response.status };
  } catch (error) {
    throw new Error(`Upload failed: ${error.message}`);
  }
};


export async function submitPackage(data, signal) {
  const url = `${API_BASE}/admin/data`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    signal,
  });
  const object = await response.json();
  if (object.status !== 201) {
    throw new Error(object.error || `HTTP error in submitPackage Status: ${object.status}`);
  }
  if (!object || typeof object !== 'object' || !('package_id' in object)) {
    throw new Error('Invalid object format from submitPackage');
  }
  return object;
}

export async function updatePackage(data, signal) {
  const response = await fetch(`${API_BASE}/admin/data-update/${data.packageId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    signal,
  });
  if (!response.ok) throw new Error('Failed to update package');
  return response;
}

export const fetchPackageImages = async (packageName, signal) => {
  try {
    const response = await fetch(`${API_BASE}/admin/package-images/${encodeURIComponent(packageName)}`, {
      credentials: 'include',
      signal,
    });
    const data = await response.json();
    if (response.status !== 200) {
      throw new Error(data.error || 'Failed to fetch package images');
    }
    return data.images;
  } catch (error) {
    if (error.name !== 'AbortError') {
      throw new Error(`Failed to fetch package images: ${error.message}`);
    }
    throw error;
  }
};

export async function deletePackage(id, signal) {
  const response = await fetch(`${API_BASE}/admin/data-delete/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    signal,
  });
  if (!response.ok) throw new Error('Failed to delete package');
  return response;
}

export async function addReview(data, signal) {
  const url = `${API_BASE}/review/add`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    signal,
  });
  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.error || `HTTP error! Status: ${response.status}`);
  }
  return result;
}

export async function getReviews(data, signal) {
  const url = `${API_BASE}/review/all`;
  const response = await fetch(url, {
    method: 'GET',
    signal,
  });
  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.error || `HTTP error! Status: ${response.status}`);
  }
  return result.reviews;
}

export async function getReviewsLowerRated(data, signal) {
  const url = `${API_BASE}/review/lower-rated`;
  const response = await fetch(url, {
    method: 'GET',
    signal,
  });
  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.error || `HTTP error! Status: ${response.status}`);
  }
  return result.reviews;
}

// export async function getReviewsByDestination(data, signal) {
//   const url = `${API_BASE}/review/get-destination`;
//   const response = await fetch(url, {
//     method: 'GET',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify(data),
//     signal,
//   });
//   const result = await response.json();
//   if (!response.ok) {
//     throw new Error(result.error || `HTTP error! Status: ${response.status}`);
//   }
//   return result.reviews;
// }

export async function getLatestReviews(data, signal) {
  const url = `${API_BASE}/review/latest-reviews`;
  const response = await fetch(url, {
    method: 'GET',
    signal,
  });
  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.error || `HTTP error! Status: ${response.status}`);
  }
  return result.reviews;
}

export async function getOldestReviews(data, signal) {
  const url = `${API_BASE}/review/old-reviews`;
  const response = await fetch(url, {
    method: 'GET',
    signal,
  });
  const result = await response.json();
  if (!response.ok) {
    throw new Error(result.error || `HTTP error! Status: ${response.status}`);
  }
  return result.reviews;
}